import DesktopOnly from "@/components/ui/DesktopOnly";
import MobileOnly from "@/components/ui/MobileOnly";
import { En, Fa } from "@/components/ui/multilang";
import { NextPage } from "next";
import Image from "next/image";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";

export async function getServerSideProps({ locale }) {
  return {
    props: {
      locale,
      title: locale === "fa" ? "فرآیند گرفتن کلاس خصوصی آنلاین | درسون" : "How Darsoon Works",
      description:
        locale === "fa"
          ? "در اینجا می‌توانید نحوه انتخاب معلم خصوصی آنلاین، شرکت در کلاس‌ها و پرداخت در درسون را به راحتی مشاهده کنید."
          : "Here, you can easily see how to choose an online tutor, pay for your sessions and attend classes, ",
    },
  };
}

type Props = {
  title: string;
  description: string;
  locale: string;
};

const Page: NextPage = ({ title, description, locale }: Props) => {
  const { asPath } = useRouter();
  const baseUrl = "https://darsoon.com";
  const canonicalUrl = `${baseUrl}${locale === "fa" ? "" : "/en"}${asPath}`;
  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={canonicalUrl}
        openGraph={{
          title: title,
          description: description,
          url: canonicalUrl,
          locale: locale === "fa" ? "fa_IR" : "en_US",
          site_name: locale === "fa" ? "درسون" : "Darsoon",
          type: "website",
        }}
        additionalLinkTags={[
          {
            rel: "alternate",
            href: `${baseUrl}${asPath}`,
            hrefLang: "fa",
          },
          {
            rel: "alternate",
            href: `${baseUrl}/en${asPath}`,
            hrefLang: "en",
          },
          {
            rel: "alternate",
            href: `${baseUrl}${asPath}`,
            hrefLang: "x-default",
          },
        ]}
      />
      <div>
        <MobileOnly>
          <MobileProcess />
        </MobileOnly>
        <DesktopOnly>
          <DesktopProcess />
        </DesktopOnly>
      </div>
    </>
  );
};

export default Page;

function DesktopProcess() {
  return (
    <div className="max-w-70rem mx-auto flex flex-col gap-20">
      <En>
        <h1 className="H1">How Darsoon Works</h1>
      </En>
      <Fa>
        <h1 className="H1">درسون چگونه کار می‌کند</h1>
      </Fa>
      {/* 1 */}
      <En>
        <div className="flex justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">Welcome to Darsoon</h2>
            <p>
              Darsoon makes it easy for learners to find the perfect private online tutor. Whether you&apos;re helping
              your child excel or pursuing your own goals, explore tutor profiles, reviews, and videos to make the best
              choice. Start learning today from the comfort of home!
            </p>
          </div>
          <Image
            src="/static/process/home-en.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">به درسون خوش آمدید</h2>
            <p>
              درسون انتخاب معلم خصوصی آنلاین را برای شاگردان آسان کرده است. چه برای پیشرفت فرزندتان نیاز به معلم دارید
              یا برای رسیدن به اهداف شخصی، پروفایل‌ها، نظرات و ویدیوهای معلمان را بررسی کنید و بهترین انتخاب را داشته
              باشید. یادگیری را همراه با راحتی خانه تجربه کنید!
            </p>
          </div>
          <Image
            src="/static/process/home-fa.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>

      {/* 2 */}
      <En>
        <div className="flex flex-row-reverse justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">Tutor Selection</h2>
            <p>
              First, you choose your preferred tutor. In many subjects, there are more than one tutor. You can select
              the tutor by reviewing their profiles, watching their videos, and reading the reviews and the rating given
              by other learners.
            </p>
          </div>
          <Image
            src="/static/process/plp-en.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-row-reverse justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">انتخاب معلم خصوصی</h2>
            <p>
              ابتدا معلم مورد نظر خود را انتخاب می‌کنید. در بسیاری از موضوعات، بیش از یک معلم وجود دارد. می‌توانید با
              بررسی پروفایل‌های معلم، تماشای ویدیوهایشان، و خواندن نظرات و امتیازهای داده‌شده توسط دیگر دانش‌آموزان،
              معلم مدنظر خود را انتخاب کنید.
            </p>
          </div>
          <Image
            src="/static/process/plp-fa.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>

      {/* 3 */}
      <En>
        <div className="flex justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">Free Introductory Meeting</h2>
            <ul className="list-disc-inside">
              <li>
                Once you&apos;ve chosen a tutor, simply click &quot;Request a Free Introductory Meeting&quot; on their
                Darsoon profile page. Here are two scenarios:
                <ol className="list-decimal-inside pl-4">
                  <li>
                    If you&apos;re a returning Darsoon user, log in with your email and password, and if you need help
                    remembering your password? No problem, just reset it.
                  </li>
                  <li>
                    If you are new to Darsoon, Create an account and provide details for the students you&apos;re
                    looking after. You can include yourself in this list.
                  </li>
                </ol>
              </li>
              <li>
                Your tutor will reach out to you via WhatsApp to arrange a convenient time for an introductory meeting.
              </li>
              <li>
                After confirming a suitable time, you and the tutor will have a video call on WhatsApp. The purpose of
                this meeting is to allow you both to discuss your learning objectives and ensure that the tutor&apos;s
                teaching methods align with your expectations for the lessons.
              </li>
              <li>
                If you don&apos;t feel like the right fit after your introductory meeting, you can easily search for
                another tutor on Darsoon and return to step one.
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Image
              src="/static/process/pdp-en.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/student-en.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/intro-en.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
          </div>
        </div>
      </En>
      <Fa>
        <div className="flex justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">جلسه معارفه رایگان</h2>
            <ul className="list-disc-inside">
              <li>
                پس از انتخاب استاد معلم خصوصی، به سادگی روی «درخواست جلسه‌ی معرفی رایگان» که در پروفایل هر معلم قرار
                دارد کلیک کنید. در اینجا دو سناریو وجود دارد:
                <ol className="list-decimal-inside pr-4">
                  <li>
                    اگر از کاربران قدیمی درسون هستید، با ایمیل و رمز عبور خود وارد شوید و اگر برای به یاد آوردن رمز عبور
                    خود به کمک نیاز دارید؟ می‌توانید با استفاده از دکمه فراموشی رمز عبور خود را بازیابی کنید.
                  </li>
                  <li>
                    اگر تازه وارد درسون شده‌اید، یک حساب کاربری ایجاد کنید و اطلاعاتی مورد نیاز را برای دانش‌آموزانی که
                    مسئول آن‌ها هستید، ارائه دهید. خود شما نیز می‌توانید عضوی از لیست دانش‌آموزان باشید.
                  </li>
                </ol>
              </li>
              <li>معلم شما از طریق واتس‌آپ با شما تماس می‌گیرد تا زمان مناسبی را برای جلسه معرفی هماهنگ کند.</li>
              <li>
                پس از تایید زمان مناسب، شما و استاد راهنما در واتس‌آپ تماس تصویری خواهید داشت. هدف از این جلسه این است
                که شما در مورد اهداف یادگیری خود صحبت کنید و اطمینان حاصل کنید که روش های تدریس معلم با انتظارات شما از
                کلاس مطابقت دارد.
              </li>
              <li>
                اگر بعد از جلسه مقدماتی خود احساس کردید که معلم مدنظر برای شما مناسب نبوده، می توانید به راحتی در سایت
                درسون به دنبال معلم دیگری بگردید و به مرحله اول بازگردید.
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Image
              src="/static/process/pdp-fa.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/student-fa.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/intro-fa.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
          </div>
        </div>
      </Fa>

      {/* 4 */}
      <En>
        <div className="flex flex-row-reverse justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">Payment and Class Registration</h2>
            <p>
              After the introductory meeting, if you decide to take lessons with the tutor, please go to the
              tutor&apos;s page on our website. There, you can choose and purchase the package that best suits your
              needs. To do this, follow the steps outlined below:
            </p>
            <ul className="list-disc-inside">
              <li>Click on the &quot;Booking and Payment&quot; button.</li>
              <li>
                A pop-up will appear where you can choose the course you want to book. Courses are listed by
                students&apos; names. Pick your desired course and specify the number of sessions you&apos;d like. The
                total cost will be displayed. To complete your booking, simply enter your credit card information and
                click the &quot;Pay&quot; button powered by Square. If you can&apos;t find your desired course or number
                of sessions you are looking for, please get in touch with us.
              </li>
              <li>You will now receive your payment receipt by email.</li>
            </ul>
          </div>
          <Image
            src="/static/process/pay-en.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-row-reverse justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">پرداخت و ثبت نام کلاس</h2>
            <p>
              پس از جلسه معرفی، اگر تصمیم گرفتید که با معلم کلاس بگیرید، لطفاً به صفحه معلم در وب‌سایت ما سر بزنید. در
              آنجا می‌توانید بسته‌ای را انتخاب و خریداری کنید که به بهترین شکل با نیازهای شما مطابقت دارد. برای انجام
              این کار، مراحل زیر را دنبال کنید:
            </p>
            <ul className="list-disc-inside">
              <li>بر روی دکمه «رزرو و پرداخت» کلیک کنید.</li>
              <li>
                یک پاپ‌آپ ظاهر می‌شود که در آن می توانید دوره ای را که می خواهید رزرو کنید، انتخاب کنید. دوره‌ها متناسب
                با نام دانش‌آموزان مشخص شده‌اند. دوره‌ی مورد نظر خود را انتخاب کنید و تعداد جلسات خود را نیز مشخص کنید.
                هزینه‌ی کل نمایش داده خواهد شد. برای تکمیل رزرو خود، کافی است اطلاعات کارت اعتباری خود را وارد کنید و
                روی دکمه «پرداخت» که توسط Square ارائه شده است، کلیک کنید. اگر نتوانستید دوره‌ی مورد نظر یا تعداد جلسات
                مطلوب خود را پیدا کنید، لطفا با ما تماس بگیرید.
              </li>
              <li>اکنون رسید پرداخت خود را از طریق ایمیل دریافت خواهید کرد.</li>
            </ul>
          </div>
          <Image
            src="/static/process/pay-fa.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>

      {/* 5 */}
      <En>
        <div className="flex justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">Getting Started with Your Lessons</h2>
            <ul className="list-disc-inside">
              <li>
                Once you&apos;ve purchased a package, you can get in touch with your tutor to schedule your classes.
              </li>
              <li>
                Before diving in, make sure to check out our FAQ section. It&apos;s got important information,
                especially about our cancellation policy.
              </li>
            </ul>
          </div>
          <Image
            src="/static/process/start.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">کلاس خود را شروع کنید</h2>
            <ul className="list-disc-inside">
              <li>پس از خرید بسته، می‌توانید برای نهایی کردن ساعت کلاس‌ها با معلم خود تماس بگیرید.</li>
              <li>
                قبل از شروع کلاس‌ها، حتماً بخش سؤالات متداول ما را بررسی کنید. اطلاعات مهمی به خصوص در مورد قوانین لغو
                کلاس در درسون وجود دارد.
              </li>
            </ul>
          </div>
          <Image
            src="/static/process/start.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>
      {/* 6 */}
      <En>
        <div className="flex flex-row-reverse justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">Share Your Experience by Providing Feedback</h2>
            <p>
              After a few lessons, we’ll send you an email asking for your thoughts. You can also share your opinions
              directly on your tutor’s profile or within your account anytime. Your input helps us enhance our services
              and provides valuable guidance for other students.
            </p>
          </div>
          <Image
            src="/static/process/feedback-en.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-row-reverse justify-between items-start gap-40">
          <div>
            <h2 className="H2 mb-4">تجربه خود را با ما به اشتراک بگذارید</h2>
            <p>
              پس از گذشت چند جلسه از کلاس خود، ما یک ایمیل برای شما ارسال می‌کنیم و نظر شما را در رابطه با کلاس جویا
              خواهیم شد. همچنین می‌توانید نظرات خود را مستقیماً در پروفایل معلم خود یا در حساب خود در هر زمان با ما به
              اشتراک بگذارید. نظرات شما به ما کمک می‌کند تا خدمات خود را بهبود دهیم و راهنمایی‌های ارزشمندی را برای سایر
              دانش‌آموزان فراهم می‌کند.
            </p>
          </div>
          <Image
            src="/static/process/feedback-fa.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>
    </div>
  );
}

function MobileProcess() {
  return (
    <div className="max-w-70rem mx-auto flex flex-col gap-8">
      <En>
        <h1 className="H1">How Darsoon Works</h1>
      </En>
      <Fa>
        <h1 className="H1">درسون چگونه کار می‌کند</h1>
      </Fa>
      {/* 1 */}
      <En>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">Welcome to Darsoon</h2>
            <p>
              Darsoon makes it easy for learners to find the perfect private online tutor. Whether you&apos;re helping
              your child excel or pursuing your own goals, explore tutor profiles, reviews, and videos to make the best
              choice. Start learning today from the comfort of home!
            </p>
          </div>
          <Image
            src="/static/process/home-en-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">به درسون خوش آمدید</h2>
            <p>
              درسون انتخاب معلم خصوصی آنلاین را برای شاگردان آسان کرده است. چه برای پیشرفت فرزندتان نیاز به معلم دارید
              یا برای رسیدن به اهداف شخصی، پروفایل‌ها، نظرات و ویدیوهای معلمان را بررسی کنید و بهترین انتخاب را داشته
              باشید. یادگیری را همراه با راحتی خانه تجربه کنید!
            </p>
          </div>
          <Image
            src="/static/process/home-fa-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>

      {/* 2 */}
      <En>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">Tutor Selection</h2>
            <p>
              First, you choose your preferred tutor. In many subjects, there are more than one tutor. You can select
              the tutor by reviewing their profiles, watching their videos, and reading the reviews and the rating given
              by other learners.
            </p>
          </div>
          <Image
            src="/static/process/plp-en-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">انتخاب معلم خصوصی</h2>
            <p>
              ابتدا معلم مورد نظر خود را انتخاب می‌کنید. در بسیاری از موضوعات، بیش از یک معلم وجود دارد. می‌توانید با
              بررسی پروفایل‌های معلم، تماشای ویدیوهایشان، و خواندن نظرات و امتیازهای داده‌شده توسط دیگر دانش‌آموزان،
              معلم مدنظر خود را انتخاب کنید.
            </p>
          </div>
          <Image
            src="/static/process/plp-fa-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>

      {/* 3 */}
      <En>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">Free Introductory Meeting</h2>
            <ul className="list-disc-inside">
              <li>
                Once you&apos;ve chosen a tutor, simply click &quot;Request a Free Introductory Meeting&quot; on their
                Darsoon profile page. Here are two scenarios:
                <ol className="list-decimal-inside pl-4">
                  <li>
                    If you&apos;re a returning Darsoon user, log in with your email and password, and if you need help
                    remembering your password? No problem, just reset it.
                  </li>
                  <li>
                    If you are new to Darsoon, Create an account and provide details for the students you&apos;re
                    looking after. You can include yourself in this list.
                  </li>
                </ol>
              </li>
              <li>
                Your tutor will reach out to you via WhatsApp to arrange a convenient time for an introductory meeting.
              </li>
              <li>
                After confirming a suitable time, you and the tutor will have a video call on WhatsApp. The purpose of
                this meeting is to allow you both to discuss your learning objectives and ensure that the tutor&apos;s
                teaching methods align with your expectations for the lessons.
              </li>
              <li>
                If you don&apos;t feel like the right fit after your introductory meeting, you can easily search for
                another tutor on Darsoon and return to step one.
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Image
              src="/static/process/pdp-en-m.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/student-en-m.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/intro-en-m.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
          </div>
        </div>
      </En>
      <Fa>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">جلسه معارفه رایگان</h2>
            <ul className="list-disc-inside">
              <li>
                پس از انتخاب استاد معلم خصوصی، به سادگی روی «درخواست جلسه‌ی معرفی رایگان» که در پروفایل هر معلم قرار
                دارد کلیک کنید. در اینجا دو سناریو وجود دارد:
                <ol className="list-decimal-inside pr-4">
                  <li>
                    اگر از کاربران قدیمی درسون هستید، با ایمیل و رمز عبور خود وارد شوید و اگر برای به یاد آوردن رمز عبور
                    خود به کمک نیاز دارید؟ می‌توانید با استفاده از دکمه فراموشی رمز عبور خود را بازیابی کنید.
                  </li>
                  <li>
                    اگر تازه وارد درسون شده‌اید، یک حساب کاربری ایجاد کنید و اطلاعاتی مورد نیاز را برای دانش‌آموزانی که
                    مسئول آن‌ها هستید، ارائه دهید. خود شما نیز می‌توانید عضوی از لیست دانش‌آموزان باشید.
                  </li>
                </ol>
              </li>
              <li>معلم شما از طریق واتس‌آپ با شما تماس می‌گیرد تا زمان مناسبی را برای جلسه معرفی هماهنگ کند.</li>
              <li>
                پس از تایید زمان مناسب، شما و استاد راهنما در واتس‌آپ تماس تصویری خواهید داشت. هدف از این جلسه این است
                که شما در مورد اهداف یادگیری خود صحبت کنید و اطمینان حاصل کنید که روش های تدریس معلم با انتظارات شما از
                کلاس مطابقت دارد.
              </li>
              <li>
                اگر بعد از جلسه مقدماتی خود احساس کردید که معلم مدنظر برای شما مناسب نبوده، می توانید به راحتی در سایت
                درسون به دنبال معلم دیگری بگردید و به مرحله اول بازگردید.
              </li>
            </ul>
          </div>
          <div className="flex flex-col gap-4 w-full">
            <Image
              src="/static/process/pdp-fa-m.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/student-fa-m.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
            <Image
              src="/static/process/intro-fa-m.webp"
              alt="Home"
              width={500}
              height={350}
              className="rounded-xl shadow-xl border border-prm4"
            />
          </div>
        </div>
      </Fa>

      {/* 4 */}
      <En>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">Payment and Class Registration</h2>
            <p>
              After the introductory meeting, if you decide to take lessons with the tutor, please go to the
              tutor&apos;s page on our website. There, you can choose and purchase the package that best suits your
              needs. To do this, follow the steps outlined below:
            </p>
            <ul className="list-disc-inside">
              <li>Click on the &quot;Booking and Payment&quot; button.</li>
              <li>
                A pop-up will appear where you can choose the course you want to book. Courses are listed by
                students&apos; names. Pick your desired course and specify the number of sessions you&apos;d like. The
                total cost will be displayed. To complete your booking, simply enter your credit card information and
                click the &quot;Pay&quot; button powered by Square. If you can&apos;t find your desired course or number
                of sessions you are looking for, please get in touch with us.
              </li>
              <li>You will now receive your payment receipt by email.</li>
            </ul>
          </div>
          <Image
            src="/static/process/pay-en-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">پرداخت و ثبت نام کلاس</h2>
            <p>
              پس از جلسه معرفی، اگر تصمیم گرفتید که با معلم کلاس بگیرید، لطفاً به صفحه معلم در وب‌سایت ما سر بزنید. در
              آنجا می‌توانید بسته‌ای را انتخاب و خریداری کنید که به بهترین شکل با نیازهای شما مطابقت دارد. برای انجام
              این کار، مراحل زیر را دنبال کنید:
            </p>
            <ul className="list-disc-inside">
              <li>بر روی دکمه «رزرو و پرداخت» کلیک کنید.</li>
              <li>
                یک پاپ‌آپ ظاهر می‌شود که در آن می توانید دوره ای را که می خواهید رزرو کنید، انتخاب کنید. دوره‌ها متناسب
                با نام دانش‌آموزان مشخص شده‌اند. دوره‌ی مورد نظر خود را انتخاب کنید و تعداد جلسات خود را نیز مشخص کنید.
                هزینه‌ی کل نمایش داده خواهد شد. برای تکمیل رزرو خود، کافی است اطلاعات کارت اعتباری خود را وارد کنید و
                روی دکمه «پرداخت» که توسط Square ارائه شده است، کلیک کنید. اگر نتوانستید دوره‌ی مورد نظر یا تعداد جلسات
                مطلوب خود را پیدا کنید، لطفا با ما تماس بگیرید.
              </li>
              <li>اکنون رسید پرداخت خود را از طریق ایمیل دریافت خواهید کرد.</li>
            </ul>
          </div>
          <Image
            src="/static/process/pay-fa-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>

      {/* 5 */}
      <En>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">Getting Started with Your Lessons</h2>
            <ul className="list-disc-inside">
              <li>
                Once you&apos;ve purchased a package, you can get in touch with your tutor to schedule your classes.
              </li>
              <li>
                Before diving in, make sure to check out our FAQ section. It&apos;s got important information,
                especially about our cancellation policy.
              </li>
            </ul>
          </div>
          <Image
            src="/static/process/start.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">کلاس خود را شروع کنید</h2>
            <ul className="list-disc-inside">
              <li>پس از خرید بسته، می‌توانید برای نهایی کردن ساعت کلاس‌ها با معلم خود تماس بگیرید.</li>
              <li>
                قبل از شروع کلاس‌ها، حتماً بخش سؤالات متداول ما را بررسی کنید. اطلاعات مهمی به خصوص در مورد قوانین لغو
                کلاس در درسون وجود دارد.
              </li>
            </ul>
          </div>
          <Image
            src="/static/process/start.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>
      {/* 6 */}
      <En>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">Share Your Experience by Providing Feedback</h2>
            <p>
              After a few lessons, we’ll send you an email asking for your thoughts. You can also share your opinions
              directly on your tutor’s profile or within your account anytime. Your input helps us enhance our services
              and provides valuable guidance for other students.
            </p>
          </div>
          <Image
            src="/static/process/feedback-en-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </En>
      <Fa>
        <div className="flex flex-col gap-4">
          <div>
            <h2 className="H2 mb-4">تجربه خود را با ما به اشتراک بگذارید</h2>
            <p>
              پس از گذشت چند جلسه از کلاس خود، ما یک ایمیل برای شما ارسال می‌کنیم و نظر شما را در رابطه با کلاس جویا
              خواهیم شد. همچنین می‌توانید نظرات خود را مستقیماً در پروفایل معلم خود یا در حساب خود در هر زمان با ما به
              اشتراک بگذارید. نظرات شما به ما کمک می‌کند تا خدمات خود را بهبود دهیم و راهنمایی‌های ارزشمندی را برای سایر
              دانش‌آموزان فراهم می‌کند.
            </p>
          </div>
          <Image
            src="/static/process/feedback-fa-m.webp"
            alt="Home"
            width={500}
            height={350}
            className="rounded-xl shadow-xl border border-prm4"
          />
        </div>
      </Fa>
    </div>
  );
}
